import * as React from "react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  AlertTriangle,
  CheckCircle2,
  Coffee,
  Loader2,
  Plus,
  Sparkles,
  Sunrise,
  Trash2,
  X,
} from "lucide-react";
import type {
  BreakConfigPayload,
  BreakKind,
  CatalogueReadinessView,
  DayPatternKind,
  DayPatternPayload,
  PeriodZeroPayload,
  QuickGenerateRequest,
  SchedulingWeekday,
  ValidityKind,
} from "@eduviaos/shared";
import { useCatalogueReadiness, useQuickGenerateTimetableJob } from "@/hooks/useTimetable";

/**
 * Phase G3.5 FE-1 — Quick-generate sheet (multi-break + per-day
 * patterns + validity).
 *
 * Replaces the simpler G3 FE-2 sheet that only exposed a single
 * lunch break and a Saturday-only day pattern. The new shape lets
 * IA admins:
 *
 *  • Add as many breaks as their school runs (short, long, lunch,
 *    prayer, assembly, …) anchored to any 1-based "after period N".
 *  • Override any weekday's pattern (FULL, HALF, OFF, ALTERNATE),
 *    not just Saturday — supports schools where Friday is the
 *    weekly holiday or where alternate Wednesdays are off.
 *  • Pick a validity window from a semantic list (weekly,
 *    fortnightly, monthly, term, semester, yearly, custom). The BE
 *    derives `effective_to` from the chosen kind unless a custom
 *    date is supplied.
 *
 * The sheet remains a single scrollable surface (no tabs) so the
 * full configuration is visible at a glance — admins generate
 * timetables rarely (once per term/year) so the friction of a
 * longer form is acceptable, and a tabbed flow hides validation
 * errors that span multiple sections.
 */

const ALL_WEEKDAYS: SchedulingWeekday[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const WEEKDAY_LABEL: Record<SchedulingWeekday, string> = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun",
};

const BREAK_KIND_LABEL: Record<BreakKind, string> = {
  short: "Short break",
  long: "Long break",
  lunch: "Lunch",
  prayer: "Prayer",
  assembly: "Assembly",
  other: "Other",
};

const DAY_PATTERN_LABEL: Record<DayPatternKind, string> = {
  full: "Full day",
  half: "Half day",
  off: "Off / Holiday",
  alternate: "Alternate weeks",
};

const VALIDITY_KIND_LABEL: Record<ValidityKind, string> = {
  weekly: "Weekly",
  fortnightly: "Fortnightly (2 weeks)",
  monthly: "Monthly",
  term: "Term (≈ 3 months)",
  semester: "Semester (≈ 6 months)",
  yearly: "Yearly",
  custom: "Custom range",
};

const VALIDITY_KINDS: ValidityKind[] = [
  "weekly",
  "fortnightly",
  "monthly",
  "term",
  "semester",
  "yearly",
  "custom",
];

interface BreakRow extends Required<
  Pick<BreakConfigPayload, "after_period" | "duration_min" | "label">
> {
  kind: BreakKind;
  /** G3.6 BE-1 — when `true`, anchor BEFORE the zero period
   * (assembly / prayer / announcements). Greyed out and forced to
   * `false` when the zero period is disabled. */
  before_period_zero: boolean;
}

interface DayPatternRow {
  weekday: SchedulingWeekday;
  pattern: DayPatternKind;
  alternate_parity: "odd" | "even";
}

interface QuickGenerateFormState {
  name: string;
  description: string;
  branchId: string;
  academicYearId: string;
  workingDays: SchedulingWeekday[];
  periodsPerDay: number;
  periodLengthMin: number;
  breaks: BreakRow[];
  dayPatterns: DayPatternRow[];
  /** G3.6 BE-1 — homeroom slot at the start of every working day. */
  periodZeroEnabled: boolean;
  periodZeroDurationMin: number;
  periodZeroLabel: string;
  /** G3.6 BE-1 — `null` ⇒ no per-day cap (the workload-balance
   * soft constraint still spreads load). The toggle drives this:
   * "Cap teacher load" off ⇒ both fields are sent as `null`. */
  enforceTeacherCaps: boolean;
  teacherMaxHoursPerDay: number;
  teacherMaxHoursPerWeek: number;
  validityKind: ValidityKind;
  validFrom: string;
  validUntil: string;
}

const DEFAULT_BREAKS: BreakRow[] = [
  {
    after_period: 2,
    duration_min: 10,
    label: "Short break",
    kind: "short",
    before_period_zero: false,
  },
  {
    after_period: 4,
    duration_min: 30,
    label: "Lunch",
    kind: "lunch",
    before_period_zero: false,
  },
];

const DEFAULT_FORM: QuickGenerateFormState = {
  name: "",
  description: "",
  branchId: "",
  academicYearId: "",
  workingDays: ["MON", "TUE", "WED", "THU", "FRI"],
  periodsPerDay: 6,
  periodLengthMin: 40,
  breaks: DEFAULT_BREAKS,
  dayPatterns: [],
  periodZeroEnabled: false,
  periodZeroDurationMin: 15,
  periodZeroLabel: "Zero period",
  enforceTeacherCaps: true,
  teacherMaxHoursPerDay: 5,
  teacherMaxHoursPerWeek: 30,
  validityKind: "yearly",
  validFrom: "",
  validUntil: "",
};

const READINESS_GATE_LABEL: Record<CatalogueReadinessView["missing"][number], string> = {
  subjects: "Add subjects to your academic catalogue",
  sections: "Create at least one section / class",
  teacher_eligibility: "Onboard teachers and tag their subject eligibility",
  grade_demand: "Map subjects to every section's grade",
};

interface QuickGenerateSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function QuickGenerateSheet({ open, onOpenChange }: QuickGenerateSheetProps) {
  const [form, setForm] = React.useState<QuickGenerateFormState>(DEFAULT_FORM);
  const [error, setError] = React.useState<string | null>(null);
  const mutation = useQuickGenerateTimetableJob();

  // G3.6 BE-1 — pre-flight readiness check. The hook only fires
  // once both ID fields have content, so the banner stays neutral
  // while the admin is typing.
  const readinessQuery = useCatalogueReadiness({
    branchId: form.branchId,
    academicYearId: form.academicYearId,
  });
  const readiness = readinessQuery.data ?? null;
  const readinessReady = readiness?.ready === true;
  const readinessHasIds = Boolean(form.branchId.trim() && form.academicYearId.trim());

  React.useEffect(() => {
    if (open) {
      setForm(DEFAULT_FORM);
      setError(null);
      mutation.reset();
    }
    // mutation.reset is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleDay = (day: SchedulingWeekday) => {
    setForm((prev) => {
      const isOn = prev.workingDays.includes(day);
      const nextDays = isOn
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day];
      // When a day is removed from working_days, drop any pattern
      // override for it — keeping the override would be confusing
      // and the BE rejects pattern entries for non-working days.
      const nextPatterns = isOn
        ? prev.dayPatterns.filter((dp) => dp.weekday !== day)
        : prev.dayPatterns;
      return { ...prev, workingDays: nextDays, dayPatterns: nextPatterns };
    });
  };

  const addBreak = () => {
    setForm((prev) => ({
      ...prev,
      breaks: [
        ...prev.breaks,
        {
          after_period: Math.max(1, prev.breaks.length + 1),
          duration_min: 10,
          label: "Break",
          kind: "other",
          before_period_zero: false,
        },
      ],
    }));
  };

  const updateBreak = (index: number, patch: Partial<BreakRow>) => {
    setForm((prev) => {
      const next = prev.breaks.slice();
      const current = next[index];
      if (!current) return prev;
      next[index] = { ...current, ...patch };
      return { ...prev, breaks: next };
    });
  };

  const removeBreak = (index: number) => {
    setForm((prev) => ({ ...prev, breaks: prev.breaks.filter((_, i) => i !== index) }));
  };

  const setDayPattern = (day: SchedulingWeekday, pattern: DayPatternKind) => {
    setForm((prev) => {
      const others = prev.dayPatterns.filter((dp) => dp.weekday !== day);
      // Pattern === "full" is the implicit default — drop the row
      // so we only send entries that actually deviate.
      if (pattern === "full") {
        return { ...prev, dayPatterns: others };
      }
      const existing = prev.dayPatterns.find((dp) => dp.weekday === day);
      return {
        ...prev,
        dayPatterns: [
          ...others,
          {
            weekday: day,
            pattern,
            alternate_parity: existing?.alternate_parity ?? "odd",
          },
        ],
      };
    });
  };

  const setAlternateParity = (day: SchedulingWeekday, parity: "odd" | "even") => {
    setForm((prev) => ({
      ...prev,
      dayPatterns: prev.dayPatterns.map((dp) =>
        dp.weekday === day ? { ...dp, alternate_parity: parity } : dp,
      ),
    }));
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return "Name is required.";
    if (!form.branchId.trim()) return "Branch ID is required.";
    if (!form.academicYearId.trim()) return "Academic year ID is required.";
    if (form.workingDays.length === 0) return "Pick at least one working day.";
    if (form.periodsPerDay < 1 || form.periodsPerDay > 20)
      return "Periods per day must be between 1 and 20.";
    if (form.periodLengthMin < 10 || form.periodLengthMin > 240)
      return "Period length must be between 10 and 240 minutes.";

    if (form.periodZeroEnabled) {
      if (form.periodZeroDurationMin < 5 || form.periodZeroDurationMin > 60) {
        return "Zero period duration must be between 5 and 60 minutes.";
      }
      const label = form.periodZeroLabel.trim();
      if (label.length === 0 || label.length > 40) {
        return "Zero period label must be between 1 and 40 characters.";
      }
    }

    for (let i = 0; i < form.breaks.length; i += 1) {
      const b = form.breaks[i]!;
      if (b.after_period < 0 || b.after_period > form.periodsPerDay) {
        return `Break #${i + 1}: anchor period must be between 0 and ${form.periodsPerDay}.`;
      }
      if (b.duration_min < 1 || b.duration_min > 240) {
        return `Break #${i + 1}: duration must be between 1 and 240 minutes.`;
      }
      if (!b.label.trim()) {
        return `Break #${i + 1}: label is required.`;
      }
      if (b.before_period_zero && !form.periodZeroEnabled) {
        return `Break #${i + 1}: enable the zero period to anchor a break before it.`;
      }
    }

    if (form.enforceTeacherCaps) {
      if (form.teacherMaxHoursPerDay < 1 || form.teacherMaxHoursPerDay > 12) {
        return "Max hours / day must be between 1 and 12.";
      }
      if (form.teacherMaxHoursPerWeek < 1 || form.teacherMaxHoursPerWeek > 60) {
        return "Max hours / week must be between 1 and 60.";
      }
    }

    if (form.validityKind === "custom") {
      if (!form.validFrom.trim()) return "Custom validity needs a start date.";
      if (!form.validUntil.trim()) return "Custom validity needs an end date.";
      if (form.validUntil <= form.validFrom) {
        return "Validity end date must be strictly after the start date.";
      }
    } else if (form.validFrom && form.validUntil && form.validUntil <= form.validFrom) {
      return "Validity end date must be strictly after the start date.";
    }

    if (readinessHasIds && readiness && !readiness.ready) {
      return "Catalogue isn't ready — resolve the prerequisites listed above before submitting.";
    }

    return null;
  };

  const buildPayload = (): QuickGenerateRequest => {
    const sortedDays = [...form.workingDays].sort(
      (a, b) => ALL_WEEKDAYS.indexOf(a) - ALL_WEEKDAYS.indexOf(b),
    );
    const breaks: BreakConfigPayload[] = form.breaks.map((b) => ({
      after_period: b.after_period,
      duration_min: b.duration_min,
      label: b.label.trim() || "Break",
      kind: b.kind,
      // Only emit `before_period_zero=true` when the zero period
      // is enabled — the BE rejects the combination otherwise and
      // dropping the flag entirely keeps the payload tidy.
      ...(b.before_period_zero && form.periodZeroEnabled ? { before_period_zero: true } : {}),
    }));
    const dayPatterns: DayPatternPayload[] = form.dayPatterns
      .filter((dp) => sortedDays.includes(dp.weekday))
      .map((dp) => ({
        weekday: dp.weekday,
        pattern: dp.pattern,
        ...(dp.pattern === "alternate" ? { alternate_parity: dp.alternate_parity } : {}),
      }));

    const periodZero: PeriodZeroPayload | undefined = form.periodZeroEnabled
      ? {
          enabled: true,
          duration_min: form.periodZeroDurationMin,
          label: form.periodZeroLabel.trim() || "Zero period",
        }
      : undefined;

    const payload: QuickGenerateRequest = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      branch_id: form.branchId.trim(),
      academic_year_id: form.academicYearId.trim(),
      config: {
        working_days: sortedDays,
        periods_per_day: form.periodsPerDay,
        period_length_min: form.periodLengthMin,
        breaks,
        day_patterns: dayPatterns,
        ...(periodZero ? { period_zero: periodZero } : {}),
        teacher_max_hours_per_day: form.enforceTeacherCaps ? form.teacherMaxHoursPerDay : null,
        teacher_max_hours_per_week: form.enforceTeacherCaps ? form.teacherMaxHoursPerWeek : null,
      },
      validity_kind: form.validityKind,
      valid_from: form.validFrom || null,
      valid_until: form.validUntil || null,
    };
    return payload;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);

    try {
      const job = await mutation.mutateAsync(buildPayload());
      toast.success(
        `Queued "${job?.name ?? form.name.trim()}" — track progress in the Solver jobs list.`,
      );
      onOpenChange(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to queue the schedule.";
      setError(message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-xl"
        data-testid="quick-generate-sheet"
      >
        <SheetHeader>
          <SheetTitle>New schedule</SheetTitle>
          <SheetDescription>
            Builds a draft timetable from your active subjects, sections, and teacher availability.
            Configure the period shape, breaks, per-day overrides, and validity below.
          </SheetDescription>
        </SheetHeader>

        <ReadinessBanner
          isLoading={readinessQuery.isLoading || readinessQuery.isFetching}
          hasIds={readinessHasIds}
          readiness={readiness}
        />

        <div className="space-y-6 py-4">
          {/* ── Identity ───────────────────────────────────────────── */}
          <section className="space-y-4">
            <SectionHeading>Identity</SectionHeading>
            <div className="space-y-2">
              <Label htmlFor="qg-name">Name</Label>
              <Input
                id="qg-name"
                placeholder="e.g. Spring 2026 — first draft"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                maxLength={120}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qg-description">Description (optional)</Label>
              <Textarea
                id="qg-description"
                placeholder="Notes for reviewers — what's special about this run."
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={2}
                maxLength={2000}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="qg-branch">Branch ID</Label>
                <Input
                  id="qg-branch"
                  placeholder="branch-default"
                  value={form.branchId}
                  onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}
                  maxLength={64}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qg-year">Academic year</Label>
                <Input
                  id="qg-year"
                  placeholder="ay-2026-27"
                  value={form.academicYearId}
                  onChange={(e) => setForm((p) => ({ ...p, academicYearId: e.target.value }))}
                  maxLength={64}
                />
              </div>
            </div>
          </section>

          {/* ── Working days + period grid ─────────────────────────── */}
          <section className="space-y-4">
            <SectionHeading>Period grid</SectionHeading>
            <div className="space-y-2">
              <Label>Working days</Label>
              <div className="flex flex-wrap gap-2">
                {ALL_WEEKDAYS.map((day) => {
                  const active = form.workingDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={cn(
                        "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-muted-foreground hover:bg-muted",
                      )}
                      aria-pressed={active}
                      data-testid={`qg-day-${day}`}
                    >
                      {WEEKDAY_LABEL[day]}
                    </button>
                  );
                })}
              </div>
              <p className="text-muted-foreground text-xs">
                Tap any weekday to mark it as working. Tap again to mark it as the weekly holiday —
                works for any day, not just Saturday and Sunday.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="qg-periods">Periods / day</Label>
                <Input
                  id="qg-periods"
                  type="number"
                  min={1}
                  max={20}
                  value={form.periodsPerDay}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, periodsPerDay: Number(e.target.value) || 0 }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qg-length">Period length (min)</Label>
                <Input
                  id="qg-length"
                  type="number"
                  min={10}
                  max={240}
                  value={form.periodLengthMin}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, periodLengthMin: Number(e.target.value) || 0 }))
                  }
                />
              </div>
            </div>
          </section>

          {/* ── Zero period (homeroom) ─────────────────────────────── */}
          <section className="space-y-3" data-testid="qg-period-zero">
            <div className="flex items-center justify-between">
              <SectionHeading>Zero period (homeroom)</SectionHeading>
              <Switch
                aria-label="Enable zero period"
                checked={form.periodZeroEnabled}
                onCheckedChange={(checked) =>
                  setForm((p) => ({
                    ...p,
                    periodZeroEnabled: checked,
                    breaks: checked
                      ? p.breaks
                      : p.breaks.map((b) => ({ ...b, before_period_zero: false })),
                  }))
                }
                data-testid="qg-period-zero-toggle"
              />
            </div>
            <p className="text-muted-foreground text-xs">
              A short slot at the start of every working day reserved for the class teacher —
              attendance, announcements, or pastoral check-ins. Schools that run morning assembly or
              prayer can anchor a break BEFORE this slot using the toggle on each break row.
            </p>
            {form.periodZeroEnabled ? (
              <div className="border-border/60 grid grid-cols-2 gap-3 rounded-md border p-3">
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor="qg-period-zero-duration">
                    Duration (min)
                  </Label>
                  <Input
                    id="qg-period-zero-duration"
                    type="number"
                    min={5}
                    max={60}
                    value={form.periodZeroDurationMin}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        periodZeroDurationMin: Number(e.target.value) || 0,
                      }))
                    }
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor="qg-period-zero-label">
                    Label
                  </Label>
                  <Input
                    id="qg-period-zero-label"
                    value={form.periodZeroLabel}
                    onChange={(e) => setForm((p) => ({ ...p, periodZeroLabel: e.target.value }))}
                    maxLength={40}
                    className="h-8 text-xs"
                    placeholder="Zero period"
                  />
                </div>
              </div>
            ) : null}
          </section>

          {/* ── Breaks ─────────────────────────────────────────────── */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionHeading>Breaks</SectionHeading>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={addBreak}
                data-testid="qg-add-break"
              >
                <Plus aria-hidden="true" className="size-4" />
                Add break
              </Button>
            </div>
            {form.breaks.length === 0 ? (
              <div className="border-border/60 text-muted-foreground rounded-md border border-dashed p-3 text-center text-xs">
                No breaks configured — the entire day will be filled with periods.
              </div>
            ) : (
              <ul className="space-y-2">
                {form.breaks.map((b, index) => (
                  <li
                    key={index}
                    className="border-border/60 space-y-2 rounded-md border p-3"
                    data-testid={`qg-break-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      <Coffee aria-hidden="true" className="text-muted-foreground size-4" />
                      <Input
                        aria-label={`Break ${index + 1} label`}
                        value={b.label}
                        onChange={(e) => updateBreak(index, { label: e.target.value })}
                        maxLength={80}
                        className="flex-1"
                        placeholder="e.g. Lunch"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeBreak(index)}
                        aria-label={`Remove break ${index + 1}`}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`qg-break-${index}-kind`}>
                          Kind
                        </Label>
                        <Select
                          value={b.kind}
                          onValueChange={(v) => updateBreak(index, { kind: v as BreakKind })}
                        >
                          <SelectTrigger id={`qg-break-${index}-kind`} className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(Object.keys(BREAK_KIND_LABEL) as BreakKind[]).map((k) => (
                              <SelectItem key={k} value={k}>
                                {BREAK_KIND_LABEL[k]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`qg-break-${index}-after`}>
                          After period
                        </Label>
                        <Input
                          id={`qg-break-${index}-after`}
                          type="number"
                          min={0}
                          max={form.periodsPerDay}
                          value={b.after_period}
                          onChange={(e) =>
                            updateBreak(index, { after_period: Number(e.target.value) || 0 })
                          }
                          disabled={b.before_period_zero}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`qg-break-${index}-dur`}>
                          Duration (min)
                        </Label>
                        <Input
                          id={`qg-break-${index}-dur`}
                          type="number"
                          min={1}
                          max={240}
                          value={b.duration_min}
                          onChange={(e) =>
                            updateBreak(index, { duration_min: Number(e.target.value) || 0 })
                          }
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                    <label
                      className={cn(
                        "flex items-center gap-2 text-xs",
                        form.periodZeroEnabled
                          ? "text-foreground"
                          : "text-muted-foreground/70 cursor-not-allowed",
                      )}
                    >
                      <Switch
                        checked={b.before_period_zero}
                        onCheckedChange={(checked) =>
                          updateBreak(index, { before_period_zero: checked })
                        }
                        disabled={!form.periodZeroEnabled}
                        aria-label={`Anchor break ${index + 1} before the zero period`}
                        data-testid={`qg-break-${index}-before-zero`}
                      />
                      <Sunrise className="size-3.5" aria-hidden="true" />
                      <span>Anchor before the zero period (assembly / prayer)</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-muted-foreground text-xs">
              Anchor a break "after period 0" to schedule it between the zero period and period 1
              (e.g. attendance break). To run morning assembly or prayer BEFORE the zero period,
              enable the zero period above and toggle "Anchor before the zero period" on the
              relevant row.
            </p>
          </section>

          {/* ── Per-day patterns ───────────────────────────────────── */}
          <section className="space-y-3">
            <SectionHeading>Per-day patterns</SectionHeading>
            <p className="text-muted-foreground text-xs">
              Override the shape of any working day — half-day Saturdays, alternate Wednesdays,
              prayer-only Fridays, etc. Days left on "Full day" run the standard period count.
            </p>
            <ul className="space-y-2">
              {form.workingDays
                .slice()
                .sort((a, b) => ALL_WEEKDAYS.indexOf(a) - ALL_WEEKDAYS.indexOf(b))
                .map((day) => {
                  const override = form.dayPatterns.find((dp) => dp.weekday === day);
                  const pattern: DayPatternKind = override?.pattern ?? "full";
                  return (
                    <li
                      key={day}
                      className="border-border/60 flex flex-wrap items-center gap-3 rounded-md border p-2"
                    >
                      <div className="w-12 text-xs font-medium uppercase">{WEEKDAY_LABEL[day]}</div>
                      <Select
                        value={pattern}
                        onValueChange={(v) => setDayPattern(day, v as DayPatternKind)}
                      >
                        <SelectTrigger
                          className="h-8 w-40 text-xs"
                          data-testid={`qg-day-pattern-${day}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(DAY_PATTERN_LABEL) as DayPatternKind[]).map((k) => (
                            <SelectItem key={k} value={k}>
                              {DAY_PATTERN_LABEL[k]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {pattern === "alternate" ? (
                        <Select
                          value={override?.alternate_parity ?? "odd"}
                          onValueChange={(v) => setAlternateParity(day, v as "odd" | "even")}
                        >
                          <SelectTrigger className="h-8 w-32 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="odd">Odd weeks</SelectItem>
                            <SelectItem value="even">Even weeks</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : null}
                    </li>
                  );
                })}
            </ul>
          </section>

          {/* ── Validity ───────────────────────────────────────────── */}
          <section className="space-y-3">
            <SectionHeading>Validity</SectionHeading>
            <div className="space-y-2">
              <Label htmlFor="qg-validity-kind">Validity window</Label>
              <Select
                value={form.validityKind}
                onValueChange={(v) => setForm((p) => ({ ...p, validityKind: v as ValidityKind }))}
              >
                <SelectTrigger id="qg-validity-kind" data-testid="qg-validity-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VALIDITY_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {VALIDITY_KIND_LABEL[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                Anything other than <strong>Custom range</strong> derives the end date from the
                start date automatically.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="qg-valid-from">Valid from</Label>
                <Input
                  id="qg-valid-from"
                  type="date"
                  value={form.validFrom}
                  onChange={(e) => setForm((p) => ({ ...p, validFrom: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qg-valid-until">
                  Valid until {form.validityKind === "custom" ? "(required)" : "(optional)"}
                </Label>
                <Input
                  id="qg-valid-until"
                  type="date"
                  value={form.validUntil}
                  onChange={(e) => setForm((p) => ({ ...p, validUntil: e.target.value }))}
                />
              </div>
            </div>
          </section>

          {/* ── Teacher caps (optional) ────────────────────────────── */}
          <section className="space-y-3" data-testid="qg-teacher-caps">
            <div className="flex items-center justify-between">
              <SectionHeading>Teacher load caps</SectionHeading>
              <Switch
                aria-label="Enforce teacher load caps"
                checked={form.enforceTeacherCaps}
                onCheckedChange={(checked) =>
                  setForm((p) => ({ ...p, enforceTeacherCaps: checked }))
                }
                data-testid="qg-teacher-caps-toggle"
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Toggle off to skip the hard per-day / per-week cap and let the workload-balance soft
              constraint spread teaching load. Useful for schools that haven't formalised teacher
              hours yet.
            </p>
            {form.enforceTeacherCaps ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="qg-tmax-day">Max hours / day</Label>
                  <Input
                    id="qg-tmax-day"
                    type="number"
                    min={1}
                    max={12}
                    value={form.teacherMaxHoursPerDay}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        teacherMaxHoursPerDay: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qg-tmax-week">Max hours / week</Label>
                  <Input
                    id="qg-tmax-week"
                    type="number"
                    min={1}
                    max={60}
                    value={form.teacherMaxHoursPerWeek}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        teacherMaxHoursPerWeek: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>
            ) : null}
          </section>

          {error ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
              data-testid="qg-error"
            >
              {error}
            </div>
          ) : null}
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            <X aria-hidden="true" className="size-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              mutation.isPending ||
              // G3.6 BE-1: hard-gate the CTA when the catalogue
              // gates aren't satisfied. The validation step gives a
              // human-readable message; this disables the button so
              // the admin can't dismiss the banner and still submit.
              (readinessHasIds && readiness !== null && !readinessReady)
            }
            data-testid="qg-submit"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="size-4" aria-hidden="true" />
            )}
            {mutation.isPending ? "Submitting…" : "Generate timetable"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground text-sm font-semibold tracking-tight">{children}</h3>;
}

interface ReadinessBannerProps {
  isLoading: boolean;
  hasIds: boolean;
  readiness: CatalogueReadinessView | null;
}

/**
 * G3.6 BE-1 — pre-flight readiness banner. Renders one of three
 * states:
 *
 *  • neutral hint  — admin hasn't typed branch / academic year yet
 *  • ready         — green check, all four gates satisfied
 *  • blocked       — amber warning + actionable list of missing
 *                    pieces (subjects, sections, teacher
 *                    eligibility, grade demand). The submit button
 *                    is disabled in this state.
 */
function ReadinessBanner({ isLoading, hasIds, readiness }: ReadinessBannerProps) {
  if (!hasIds) {
    return (
      <div
        className="border-border/60 bg-muted/30 text-muted-foreground mt-4 rounded-md border px-3 py-2 text-xs"
        data-testid="qg-readiness-pending"
      >
        Enter the branch and academic year above to run the catalogue readiness check.
      </div>
    );
  }
  if (isLoading || !readiness) {
    return (
      <div
        className="border-border/60 bg-muted/30 text-muted-foreground mt-4 flex items-center gap-2 rounded-md border px-3 py-2 text-xs"
        data-testid="qg-readiness-loading"
      >
        <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
        Checking catalogue readiness…
      </div>
    );
  }
  if (readiness.ready) {
    return (
      <div
        className="mt-4 flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300"
        data-testid="qg-readiness-ready"
      >
        <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="space-y-0.5">
          <div className="font-medium">Catalogue is ready.</div>
          <div className="text-muted-foreground">
            {readiness.subjects_count} subject{readiness.subjects_count === 1 ? "" : "s"} ·{" "}
            {readiness.sections_count} section{readiness.sections_count === 1 ? "" : "s"} ·{" "}
            {readiness.teachers_with_eligibility_count} teacher
            {readiness.teachers_with_eligibility_count === 1 ? "" : "s"} with eligibility ·{" "}
            {readiness.grades_with_demand_count} grade
            {readiness.grades_with_demand_count === 1 ? "" : "s"} with demand
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
      data-testid="qg-readiness-blocked"
      role="alert"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="space-y-1">
        <div className="font-medium">Finish onboarding before generating a timetable.</div>
        <ul className="list-inside list-disc space-y-0.5">
          {readiness.missing.map((code) => (
            <li key={code} data-testid={`qg-readiness-missing-${code}`}>
              {READINESS_GATE_LABEL[code]}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
